import React from "react";
import {
  FaClock,
  FaFlagUsa,
  FaPassport,
  FaChessRook,
} from "react-icons/fa";
import { FaGlobeEurope } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { cmsApi } from "../../services/cms";
import { useCMS } from "../../hooks/useCMS";

type VisaDetailItem = {
  title?: string;
  description?: string;
};

type VisaDestinationCard = {
  slug: string;
  title: string;
  country?: string | null;
  imageUrl?: string;
  heroImageUrl?: string;
  subtitle?: string;
  subDescription?: string;
  processingTime?: string;
  supportInfo?: string;
  highlights?: string[];
  visaDetails?: VisaDetailItem[];
  ctaText?: string;
};

const normalizeCountry = (value?: string | null) => value?.trim().toLowerCase() || "";
const siteRegion = (import.meta.env.VITE_SITE_REGION || "IN").toUpperCase();
const siteCountry = siteRegion === "AE" ? "United Arab Emirates" : "India";
const isMatchingCountry = (country?: string | null) =>
  !normalizeCountry(country) || normalizeCountry(country) === normalizeCountry(siteCountry);
const pickDetailByTitle = (visa: VisaDestinationCard, keyword: string) =>
  visa.visaDetails
    ?.find((item) => item?.title?.toLowerCase().includes(keyword))
    ?.description?.trim();

const VisaTypes: React.FC = () => {
  const { t } = useTranslation();
  const { data: visaDestinations, loading } =
    useCMS<VisaDestinationCard[]>(cmsApi.getVisaDestinations);

  if (loading) return <div className="py-20 text-center">Loading...</div>;

  const visaIcons = [
    <FaGlobeEurope size={20} className="text-current" key="schengen" />,
    <FaChessRook size={20} className="text-current" key="uk" />,
    <FaFlagUsa size={20} className="text-current" key="usa" />,
    <FaPassport size={20} className="text-current" key="australia" />,
    <FaPassport size={20} className="text-current" key="singapore" />,
  ];

  const iconStyles = [
    "bg-blue-50 text-blue-600",
    "bg-indigo-50 text-indigo-700",
    "bg-red-50 text-red-600",
    "bg-emerald-50 text-emerald-600",
    "bg-amber-50 text-amber-600",
  ];
  const filteredVisaDestinations =
    visaDestinations?.filter((visa) => isMatchingCountry(visa.country)) || [];

  return (
    <section id="popular-visa-destinations" className="bg-gray-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <h2 className="mb-4 font-heading text-3xl font-bold text-brand-dark md:text-4xl lg:text-5xl">
            {t("India Visa Destinations")}
          </h2>

          <p className="mx-auto max-w-2xl text-base text-gray-600 md:text-lg">
            {t("Choose the destination you want to apply for and our team will guide you with the right next steps.")}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {filteredVisaDestinations.map((visa, index) => {
            const highlights =
              visa.highlights
                ?.map((item) => item?.trim())
                .filter((item): item is string => Boolean(item)) || [];
            const subDescription = visa.subDescription?.trim() || "";

            const subtitle =
              visa.subtitle?.trim() ||
              highlights.find(
                (item) =>
                  item.toLowerCase() !== subDescription.toLowerCase() &&
                  !/(working\s*day|days?|weeks?|months?|hours?)/i.test(item)
              ) ||
              subDescription;

            const processingTime =
              visa.processingTime?.trim() ||
              pickDetailByTitle(visa, "processing") ||
              highlights.find((item) =>
                /(working\s*day|days?|weeks?|months?|hours?)/i.test(item)
              ) ||
              "";

            const supportInfo =
              visa.supportInfo?.trim() ||
              highlights.find(
                (item) =>
                  item !== subtitle &&
                  item !== processingTime &&
                  item.toLowerCase() !== subDescription.toLowerCase()
              ) ||
              "";

            const subtitleDisplay = subtitle || t("Visitor visa support");
            const processingDisplay = processingTime || t("As per visa timeline");
            const supportDisplay = supportInfo || t("Document preparation guidance");

            return (
              <Link
                key={visa.slug}
                to={`/visa-services/${visa.slug}`}
                className="insp-card group flex h-full min-h-[520px] flex-col overflow-hidden rounded-2xl bg-white shadow-sm hover-lift"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={visa.imageUrl || visa.heroImageUrl}
                    alt={visa.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>

                <div className="flex flex-1 flex-col justify-between p-6">
                 <div className="mb-4 flex items-start justify-between gap-4">
  <div className="flex-1">
    <h3 className="mb-2 min-h-[56px] text-xl font-bold text-brand-dark">
      {visa.title}
    </h3>

    <p className="min-h-[52px]  text-sm text-gray-500">
      {subtitleDisplay}
    </p>
  </div>

  <div
    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg ${iconStyles[index % iconStyles.length]}`}
  >
    {visaIcons[index % visaIcons.length]}
  </div>
</div>

                  <ul className="mb-6 md:h-28 space-y-3">
  <li className="flex items-start gap-3 text-sm text-gray-600">
    <FaClock
      size={16}
      className="mt-0.5 shrink-0 text-brand-pink"
    />
    <span className="leading-6">{processingDisplay}</span>
  </li>

  <li className="flex items-start gap-3 text-sm text-gray-600">
    <FaPassport
      size={16}
      className="mt-0.5 shrink-0 text-brand-pink"
    />
    <span className="leading-6">{supportDisplay}</span>
  </li>
</ul>

                  <span className="block w-full rounded-xl border border-gray-200 py-3 text-center font-medium text-brand-dark transition-colors group-hover:bg-brand-dark group-hover:text-white">
                    {visa.ctaText || t("View Details")}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default VisaTypes;
